import React, { useState } from "react";
import StageCircle from "./StageCircle.tsx";
import { ChestClosed, ChestOpen } from "../icons/ChestIcons.tsx";
import AchievementProgressBar from "./AchievementProgressBar.tsx";

type AchievementStage = {
  achievementId: number;
  title: string;
  description: string;
  goal: number;
};

type AchievementBoxProps = {
  stages: AchievementStage[];
  initialStage?: number;
  initialProgress?: number;
  darkMode?: boolean;
};

const AchievementBox: React.FC<AchievementBoxProps> = ({
  stages,
  initialStage = 0,
  initialProgress = 0,
  darkMode = false,
}) => {
  const [stage, setStage] = useState(initialStage); // 0-based
  const [progress, setProgress] = useState(initialProgress);
  const [rewarded, setRewarded] = useState(false);

  const current = stages[stage];
  const isComplete = progress >= current.goal;
  const isLastStage = stage === stages.length - 1;

  // Simulate progressing to next stage and reward
  const handleChestClick = () => {
    if (isComplete && !isLastStage) {
      setStage(stage + 1);
      setProgress(0);
      setRewarded(false);
    } else if (isComplete && isLastStage) {
      setRewarded(true);
    }
  };

  // Simulate progress for demo (remove in real app)
  const handleDemoProgress = () => {
    if (!isComplete) setProgress((p) => Math.min(current.goal, p + 1));
  };

  // Responsive size logic
  let isMobile = false;
  if (typeof window !== "undefined") {
    isMobile = window.innerWidth <= 767;
  }
  const boxPadding = isMobile ? "p-3" : "p-6";
  const boxGap = isMobile ? "gap-3" : "gap-6";
  const stageSize = isMobile ? 60 : 100;
  const stageFontSize = isMobile ? 18 : 28;
  const progressHeight = isMobile ? 16 : 28;
  const progressFontSize = isMobile ? 12 : 16;
  const chestSize = isMobile ? 24 : 32;
  const titleTextSize = isMobile ? "text-base" : "text-lg";
  const descTextSize = isMobile ? "text-sm" : "text-base";

  return (
    <div
      className={`rounded-xl shadow ${boxPadding} flex items-center ${boxGap} border ${
        darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      {/* Stage Indicator */}
      <StageCircle
        current={stage + 1}
        total={stages.length}
        isGold={
          (stages.length === 1 && progress >= current.goal && rewarded) ||
          (stages.length === 2 && progress >= current.goal && rewarded) ||
          (stages.length > 1 &&
            isLastStage &&
            progress >= current.goal &&
            rewarded)
        }
        size={stageSize}
        fontSize={stageFontSize}
        darkMode={darkMode}
      />
      {/* Achievement Info & Progress */}
      <div className="flex-1 flex flex-col gap-2">
        <div
          className={`font-bold ${titleTextSize} ${
            darkMode ? "text-gray-100" : "text-gray-700"
          }`}
          style={{ fontFamily: "Feather Bold" }}
        >
          {current.title}
        </div>
        <div
          className={`${descTextSize} ${
            darkMode ? "text-gray-300" : "text-gray-600"
          }`}
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          {current.description}
        </div>
        {/* Progress Bar */}
        <div
          className={`flex items-center ${isMobile ? "gap-2" : "gap-3"} mt-2`}
        >
          <AchievementProgressBar
            value={progress}
            max={current.goal}
            height={progressHeight}
            fontSize={progressFontSize}
            darkMode={darkMode}
          />
          {/* Chest Icon */}
          <button
            className={`ml-2 p-1 rounded-lg border-2 ${
              isComplete
                ? darkMode
                  ? "border-yellow-400 bg-yellow-500/40"
                  : "border-yellow-400 bg-yellow-200"
                : darkMode
                ? "border-gray-600 bg-gray-800"
                : "border-gray-300 bg-gray-100"
            } duration-200 transform hover:-translate-y-0.5 transition-all`}
            style={{ cursor: isComplete && !rewarded ? "pointer" : "default" }}
            disabled={!isComplete || rewarded}
            onClick={handleChestClick}
            aria-label="Claim Reward"
          >
            {isComplete ? (
              <ChestOpen size={chestSize} />
            ) : (
              <ChestClosed size={chestSize} />
            )}
          </button>
        </div>
        {/* Demo Progress Button (remove in production) */}
        {/* <button
          className="mt-2 px-3 py-1 text-xs rounded bg-blue-100 text-blue-700"
          onClick={handleDemoProgress}
          disabled={isComplete}
        >
          Tambah Progress (Demo)
        </button> */}
      </div>
    </div>
  );
};

export default AchievementBox;
